(function () {
  'use strict'

  /* @ngdoc
   * @name
   * @module
   *
   * @param {boolean} autoRefresh whether the playlist should automatically refresh to check for changes in content
   * @param {int} autoRefreshRate the rate at which the playlist should be refreshed for potentially new content
   * @param {boolean} cardSharing whether card sharing should be allowed in the playlist
   * @param {boolean} displayCart whether cart should be displayed for this playlist
   * @param {boolean} forceSingleColumn whether the content in the playlist should be displayed in a single column
   * @param {boolean} kioskMode whether this playlist should use kiosk mode
   * @param {int} playlistID the id of the playlist
   * @param {int} paginatorLimit the limit on the pagination for display
   *
   * @description
   * Configures and displays a playlist wherever it's needed
   */

  const MODULE_NAME = 'thismoment.playlist.core'
  const templateUrl = require('./tpl/playlist.html')
  const Msnry = require('masonry-layout')
  const ImagesLoaded = require('imagesloaded')

  require('angular')
    .module(MODULE_NAME, [
      require('ng-infinite-scroll'),
      require('./config/routes'),
      require('./factory/playlist-api'),
      require('../analytics'),
      require('../card'),
      require('../content'),
      require('../expandedContent'),
      require('../common/component/loader')
    ])
    .run(playlistRun)
    .component('thismomentPlaylist', {
      templateUrl: templateUrl,
      bindings: {
        autoRefresh: '<',
        autoRefreshRate: '<',
        cardOptions: '<',
        cardSharing: '<?',
        displayCart: '<?',
        displaySearch: '<?',
        displayVote: '<?',
        gridId: '=?',
        layout: '<',
        layoutOptions: '<',
        loadItems: '&',
        loaderColor: '<?',
        maxColumnCount: '<',
        paginatorLimit: '<',
        playlistId: '<',
        playlistOrder: '<',
        playlistSearch: '<',
        preloadAnalytics: '<'
      },
      controller: playlistController
    })

  playlistRun.$inject = ['$rootScope']
  function playlistRun ($rootScope) {
    if (angular.isUndefined($rootScope.paginator)) {
      $rootScope.paginator = {}
    }
  }

  playlistController.$inject = ['$injector', '$rootScope', '$scope', '$timeout', '$window', '$q', 'PlaylistContent', 'analyticsSDK', 'Content', 'ExpandedContent']
  function playlistController ($injector, $rootScope, $scope, $timeout, $window, $q, PlaylistContent, analyticsSDK, Content, ExpandedContent) {
    var vm = this

    vm.$onInit = playlistInit
    vm.startKioskManual = startKioskManual

    function playlistInit () {
      if (typeof vm.loadItems !== 'function') {
        console.warn('ERROR: You must define a loadItems function on the playlist parent controller.')
      }

      // initial configs
      vm.gridId = Math.random().toString(36).replace(/[^a-z]+/g, '')
      vm.hasLoaded = false
      vm.busy = false
      vm.isInitializing = true
      vm.stop = false
      vm.items = []
      vm.searchQuery = ''

      // promise referenced in gridRenderedCallback to delay masonry init until DOM is ready
      vm.gridRendered = $q.defer()

      // mode configs
      vm.gridMode = false
      vm.carouselMode = false
      vm.kioskMode = false
      switch (vm.layout) {
        case 'carousel':
          vm.carouselMode = true
          vm.limit = 150
          vm.offset = 0
          break
        case 'kiosk':
          vm.kioskMode = true
          vm.kioskOptions = vm.layoutOptions
          $rootScope.kioskMode = vm.kioskMode
          vm.showManualStart = vm.kioskOptions.auto_scroll
          vm.limit = 150
          vm.offset = 0
          break
        default:
          vm.gridMode = true
          vm.gridModeClick = (typeof vm.layoutOptions === 'object' && vm.layoutOptions.load_event === 'click')
          vm.limit = vm.paginatorLimit
          vm.offset = -vm.paginatorLimit
          break
      }

      // inject the GlowConfig, if this is within a glow
      if ($injector.has('GlowConfig')) {
        var GlowConfig = $injector.get('GlowConfig')
        GlowConfig.set('playlistKioskMode', vm.kioskMode)
      }

      // bind functions
      vm.getItems = getItems
      vm.loadMoreButtonClick = loadMoreButtonClick
      vm.playlistSearch = playlistSearch
      vm.playlistSearchClear = playlistSearchClear
      vm.getKioskGroups = getKioskGroups
      vm.getKioskContentToOpen = getKioskContentToOpen
      vm.kioskLoadSuccess = kioskLoadSuccess
      vm.watchForAutoContent = watchForAutoContent
      vm.gridRenderedCallback = gridRenderedCallback

      // define classes
      if (!vm.maxColumnCount) {
        vm.maxColumnCount = 1
      }
      vm.classes = {
        playlist_wrapper: {
          'force-single-column': vm.maxColumnCount === 1,
          autoScrollKiosk: vm.kioskMode && vm.kioskOptions.auto_scroll
        },
        card_wrapper: {
          'col-xs-12': true,
          'col-sm-6': vm.maxColumnCount >= 2,
          'col-md-4': vm.maxColumnCount === 3,
          'col-md-3': vm.maxColumnCount === 4,
          'col-md-2': vm.maxColumnCount === 6
        }
      }

      // init analytics
      vm.analyticsReady = $q.defer()
      if (vm.preloadAnalytics) {
        var playlist = vm.preloadAnalytics.playlist
        var playlistDate = new Date(playlist.added_timestamp)

        vm.analyticsProps = {
          'playlist id': parseInt(playlist.id, 10),
          'playlist type': playlist.type,
          'playlist source': playlist.source,
          'playlist creator': playlist.user_name,
          'playlist creation date': isNaN(playlistDate) ? '' : playlistDate.toISOString(),
          'playlist title': playlist.name,
          'playlist description': playlist.description,
          'playlist tags': playlist.tags,
          'playlist URL': $window.location.href,
          'display URL': $window.location.href
        }
        vm.cardAnalytics = {
          'display playlist': playlist.name,
          'display playlist id': playlist.id
        }

        vm.trackEvent = analyticsSDK.setupEventTracking(vm.analyticsProps)
        vm.analyticsReady.resolve()
        initGrid()
      } else {
        PlaylistContent.get({
          playlist_id: vm.playlistId,
          limit: 1,
          offset: 0,
          meta: true
        }, (response) => {
          if (response.data.playlist) {
            var playlist = response.data.playlist
            var playlistDate = new Date(playlist.added_timestamp)

            vm.analyticsProps = {
              'playlist id': parseInt(playlist.id, 10),
              'playlist type': playlist.type,
              'playlist source': playlist.source,
              'playlist creator': playlist.user_name,
              'playlist creation date': isNaN(playlistDate) ? '' : playlistDate.toISOString(),
              'playlist title': playlist.name,
              'playlist description': playlist.description,
              'playlist tags': playlist.tags,
              'playlist URL': $window.location.href,
              'display URL': $window.location.href
            }
            vm.cardAnalytics = {
              'display playlist': playlist.name,
              'display playlist id': playlist.id
            }

            vm.trackEvent = analyticsSDK.setupEventTracking(vm.analyticsProps)
            vm.analyticsReady.resolve()

            // we are ready to init the grid
            initGrid()
          }
        })
      }

      // define a listener for `modal-content-pop` calls
      $rootScope.$on('modal-content-pop', function (event, playlistId, contentId, origEvent) {
        var parentElement = angular.element(origEvent.target)
        // if correct playlist & content in playlist
        if (playlistId === vm.playlistId) {
          var foundItem = vm.items.filter((item) => {
            return item.id === contentId
          })

          if (foundItem.length > 0) {
            ExpandedContent.open(foundItem[0], vm.cardOptions, null, () => {
              parentElement[0].focus()
            })
          } else {
            PlaylistContent
              .get(
                {
                  playlist_id: playlistId,
                  content_id: contentId
                },
                function (response) {
                  if ('content' in response.data) {
                    // Content.addContent('modal-pop', response.data.content)
                    ExpandedContent.open(response.data.content, vm.cardOptions, null, () => {
                      parentElement[0].focus()
                    })
                  }
                }
              )
          }
        }
      })
    }

    $scope.$on('breakpoint:resize', function (event) {
      if (angular.isUndefined(vm.msnryGrid)) {
        return
      }
      refreshMsnryGrid()
    })

    $scope.$on('grid:refresh', function (event) {
      if (angular.isUndefined(vm.msnryGrid)) {
        return
      }
      refreshMsnryGrid()
    })

    /*
     * A function to init the masonry grid and fetch the initial items
     */
    function initGrid () {
      getItems(true)
    }

    /*
     * A function to wrap the LOAD MORE button getItems() call and to move the focus
     * to the newest content after the new content has loaded
     */
    function loadMoreButtonClick () {
      var currentCardCount = vm.items.length
      getItems(false, () => {
        $timeout(() => {
          const firstNewCard = vm.items[currentCardCount]
          const id = 'card-' + firstNewCard.id
          const card = document.getElementById(id)
          const nextAnchor = card.getElementsByTagName('a')[0]
          if (angular.isUndefined(nextAnchor)) {
            return
          }
          nextAnchor.scrollIntoView()
          nextAnchor.focus()
        }, 1000)
      })
    }

    function getItems (isInit, callback) {
      if (vm.autoRefresh) {
        var stop = vm.stop
        vm.stop = false
      }
      if (!vm.busy && !vm.stop) {
        vm.offset = vm.autoRefresh || vm.kioskMode || vm.carouselMode ? 0 : vm.offset + vm.limit
        vm.busy = true

        var params = {
          limit: vm.limit,
          offset: vm.offset,
          success: vm.autoRefresh ? autoloadSuccess : loadSuccess,
          error: loadFail
        }

        if (vm.searchQuery) {
          params.search = vm.searchQuery
          params.success = searchSuccess
        }

        if (vm.playlistOrder === 'random') {
          if (isInit) {
            vm.randSeed = Math.floor(Math.random() * (9999 - 1000) + 1000)
          }
          params.randomcontent = vm.randSeed
        }

        params.order = vm.playlistOrder

        vm.loadItems(params)
      }

      function loadSuccess (items) {
        var trigger = (isInit) ? 'initial load' : 'load more'
        Content.addContents(vm.gridId, items)

        vm.initialItems = items

        $q.when(vm.analyticsReady).then(function () {
          vm.trackEvent('Playlist Loaded', {'event trigger': trigger})
        })

        if (!items || items.length < vm.limit) {
          vm.stop = true
        }

        vm.items = Content.getContents(vm.gridId)

        $q.when(vm.gridRendered).then(function () {
          if (!vm.kioskMode) {
            $timeout(() => {
              vm.busy = false
              if (isInit) {
                vm.isInitializing = false
              }
            }, 2000)
          } else {
            kioskLoadSuccess()
          }

          loadImagesInGrid()
          if (angular.isFunction(callback)) {
            callback()
          }
        })
      }

      function searchSuccess (items) {
        var trigger = (isInit) ? 'initial search' : 'search more'
        vm.trackEvent('Playlist Loaded', {'event trigger': trigger})

        if (!items || items.length < vm.limit) {
          vm.stop = true
        }
        Content.addContents(vm.gridId, items)
        vm.items = Content.getContents(vm.gridId)
        $q.when(vm.gridRendered).then(function () {
          vm.busy = false
          if (isInit) {
            vm.isInitializing = false
          }
          loadImagesInGrid()
          if (angular.isFunction(callback)) {
            callback()
          }
        })
      }

      function autoloadSuccess (items) {
        var trigger = (isInit) ? 'initial autoload' : 'autoload more'
        var itemsToAdd = Content.filterDuplicates(vm.gridId, items)

        if (itemsToAdd.length) {
          Content.addContentsToFront(vm.gridId, itemsToAdd)
          vm.items = Content.getContents(vm.gridId)
          vm.trackEvent('Playlist Loaded', {'event trigger': trigger})
        }
        vm.stop = stop
        if (isInit) {
          vm.isInitializing = false
        }

        $q.when(vm.gridRendered).then(function () {
          if (vm.kioskMode) {
            kioskLoadSuccess()
          }
          loadImagesInGrid()
          if (angular.isFunction(callback)) {
            callback()
          }
          $timeout(vm.getItems, vm.autoRefreshRate * 1000)
        })
      }

      function loadFail (error) {
        vm.busy = false
        console.error('[ERROR] Playlist failed to load:', error)
        if (angular.isFunction(callback)) {
          callback()
        }
      }
    }

    function gridRenderedCallback () {
      if (!vm.carouselMode) {
        vm.msnryGrid = new Msnry('.grid-' + vm.gridId, {
          itemSelector: '.grid-item',
          columnWidth: '.grid-sizer',
          horizontalOrder: (vm.playlistOrder !== 'random'),
          percentPosition: true
        })
      }
      vm.gridRendered.resolve()
    }

    function loadImagesInGrid () {
      $timeout(() => {
        const imgs = ImagesLoaded('.grid-' + vm.gridId)
        imgs.on('progress', () => {
          refreshMsnryGrid()
        })
        imgs.on('always', () => {
          refreshMsnryGrid()
        })
        $timeout(() => {
          vm.hasLoaded = true
        }, 500)
      }, 0)
    }

    function refreshMsnryGrid () {
      if (!vm.carouselMode) {
        $timeout(() => {
          vm.msnryGrid.reloadItems()
          vm.msnryGrid.layout()
        }, 0)
      }
    }

    function playlistSearch () {
      Content.emptyContents(vm.gridId)
      vm.items = []
      refreshMsnryGrid()
      vm.hasLoaded = false
      vm.stop = false
      vm.limit = vm.paginatorLimit
      vm.offset = -vm.paginatorLimit
      refreshMsnryGrid()
      getItems(true)
    }

    function playlistSearchClear () {
      vm.searchQuery = ''
      Content.emptyContents(vm.gridId)
      vm.items = []
      refreshMsnryGrid()
      vm.hasLoaded = false
      vm.stop = false
      vm.limit = vm.paginatorLimit
      vm.offset = -vm.paginatorLimit
      getItems(true)
    }

    // Autoscroll Kiosk Functionality
    function startKioskManual () {
      vm.showManualStart = false
      // kioskWatchReady()
      if (vm.kioskOptions.auto_refresh_rate) {
        initKioskAutoRefresh()
      }
      setTimeout(kioskAutoScroll, 1000)
    }

    function kioskLoadSuccess () {
      vm.kioskLoaded = true
      vm.allItems = angular.copy(vm.items)
      kioskInit()
      if (vm.kioskOptions.auto_modal_open) {
        getKioskGroups()
        getKioskContentToOpen(true)
      }
      if (vm.showManualStart) {
        setTimeout(() => {
          startKioskManual()
          $scope.$apply()
        }, 5000)
      }
    }

    function initKioskAutoRefresh () {
      vm.htmlEl = document.getElementsByTagName('html')[0]
      angular.element(vm.htmlEl).addClass('smooth-scroller')
      setTimeout(function () {
        vm.refreshOnNextCycle = true
      // }, 5000) // For debugging
      }, vm.kioskOptions.auto_refresh_rate * 60 * 1000)
    }

    function kioskInit () {
      vm.busy = false
      vm.$playlist = document.getElementById('playlist-' + vm.playlistId)
      vm.$playlist.style.position = 'relative'
      vm.$playlist.style.top = '0px'
      if (vm.kioskOptions.auto_scroll) {
        vm.displayEvenOdd = 'odd'
        vm.scrollSpeed = 50 // Amount of time between scrolling by 1 px in ms
        $window.onbeforeunload = function () {
          scrollKiosk(true)
        }
      }
    }

    function kioskWatchReady () {
      if (vm.kioskOptions.auto_refresh_rate) {
        initKioskAutoRefresh()
      }
      vm.busy = false
      setTimeout(kioskAutoScroll, 1000)
    }

    function getKioskGroups () {
      var interval = vm.kioskOptions.auto_modal_open_rate
      var kioskGroupLength = Math.ceil(vm.allItems.length / interval)
      vm.kioskGroups = []
      for (var rgi = 0; rgi < kioskGroupLength; rgi++) {
        var copiedAllItems = angular.copy(vm.allItems)
        vm.kioskGroups.push(copiedAllItems.slice((interval * rgi), (interval * (rgi + 1))))
      }
    }

    function getKioskContentToOpen () {
      vm.kioskContentToOpen = []
      for (var rgi = 0; rgi < vm.kioskGroups.length; rgi++) {
        var group = vm.kioskGroups[rgi]
        var randomLimit = group.length - 1
        var groupIndex
        groupIndex = Math.floor(Math.random() * (randomLimit - 0)) + 0
        if (rgi % 2 === 0 && vm.displayEvenOdd === 'even') {
          if (group[groupIndex]) {
            vm.kioskContentToOpen.push(vm.kioskGroups[rgi][groupIndex])
            vm.kioskGroups[rgi].splice(groupIndex, 1)
          }
        } else if (rgi % 2 === 1 && vm.displayEvenOdd === 'odd') {
          if (group[groupIndex]) {
            vm.kioskContentToOpen.push(vm.kioskGroups[rgi][groupIndex])
            vm.kioskGroups[rgi].splice(groupIndex, 1)
          }
        }
      }
    }

    function kioskAutoScroll () {
      if (!vm.$playlist) {
        return
      }

      var viewportHeight = $window.innerHeight
      var playlistHeight = vm.$playlist.clientHeight
      var scrollHeight = Math.abs(parseInt(vm.$playlist.style.top, 10))

      // is the bottom of the playlist in the upper part of the viewport
      var triggerRefresh = (playlistHeight - scrollHeight) < (viewportHeight * 0.66)

      scrollKiosk(false)
      if (vm.contentOpen) {
        return
      }
      if (triggerRefresh && !angular.element(vm.$playlist).hasClass('fadeOut')) {
        if (vm.kioskOptions.auto_modal_open && $rootScope.modalOpen) {
          $rootScope.modalClose()
        }
        vm.contentOpen = false
        angular.element(vm.$playlist).addClass('fadeOut')
        // Once faded out
        setTimeout(function () {
          vm.displayEvenOdd = vm.displayEvenOdd === 'even' ? 'odd' : 'even'
          if (vm.refreshOnNextCycle) {
            vm.refreshOnNextCycle = false
            silentRefresh()
          } else {
            resetAutoKiosk()
          }
        }, 500)
      } else {
        setTimeout(kioskAutoScroll, vm.scrollSpeed)
        if (scrollHeight > 100) {
          vm.watchForAutoContent()
        }
      }
    }

    function resetAutoKiosk (contentChange) {
      if (vm.kioskOptions.auto_modal_open) {
        if (vm.kioskGroups[0].length === 0) {
          vm.getKioskGroups()
        }
        vm.getKioskContentToOpen()
      }
      vm.htmlEl.classList.remove('smooth-scroller')
      setTimeout(function () {
        scrollKiosk(true)
      }, 500)
      setTimeout(function () {
        angular.element(vm.htmlEl).addClass('smooth-scroller')
        if (contentChange) {
          kioskWatchReady()
        } else {
          angular.element(vm.$playlist).removeClass('fadeOut')
          initKioskAutoRefresh()
          kioskAutoScroll()
        }
      }, 1500)
    }

    function watchForAutoContent () {
      if (vm.kioskContentToOpen && vm.kioskContentToOpen.length) {
        try {
          var element = document.getElementById('card-' + vm.kioskContentToOpen[0].id)
          var elementBox = element.getBoundingClientRect()
          var elementTop = elementBox.top
          var elementType = vm.kioskContentToOpen[0].source.source_name || 'text-only'
          var elementClass = elementType + ' kiosk '
          switch (elementType) {
            case 'instagram':
              elementClass += 'horizontal'
              break
            case 'twitter':
              elementClass += (vm.kioskContentToOpen[0].class_name === 'text') ? 'vertical' : 'horizontal'
              break
            default:
              elementClass += 'vertical'
              break
          }
          if (element && elementTop <= $window.innerHeight * 0.75) {
            vm.contentOpen = true
            if ($rootScope.modalOpen) {
              $rootScope.modalClose()
            }
            ExpandedContent.open(vm.kioskContentToOpen[0], { kioskMode: true, layout: elementClass, size: 'lg', animation: true })
            if (vm.kioskContentToOpen[0].class_name === 'video' && vm.kioskOptions.auto_play_entire_video) {
              vm.videoEnded = false
              var minimumDisplayTime = setTimeout(function () {
                vm.videoCanClose = true
                if (vm.videoEnded) {
                  closeKioskContent()
                }
              }, vm.kioskOptions.auto_modal_open_duration * 1000)
              var videoEndedListener = $rootScope.$on('videoCompleted', function () {
                vm.videoEnded = true
                if (vm.videoCanClose) {
                  setTimeout(closeKioskContent, 500)
                }
                videoEndedListener()
                modalClosedListener()
              })
              var modalClosedListener = $rootScope.$on('modalClosed', function () {
                setTimeout(closeKioskContent, 500)
                clearTimeout(minimumDisplayTime)
                videoEndedListener()
                modalClosedListener()
              })
            } else {
              setTimeout(closeKioskContent, vm.kioskOptions.auto_modal_open_duration * 1000)
            }
            vm.kioskContentToOpen.shift()
          }
        } catch (err) {
          vm.kioskContentToOpen.shift()
        }
      }
    }

    function closeKioskContent () {
      $rootScope.modalClose()
      vm.contentOpen = false
      kioskAutoScroll()
    }

    function silentRefresh () {
      var params = {
        limit: vm.limit,
        offset: vm.offset,
        success: silentSuccess,
        error: silentFail
      }
      vm.loadItems(params)
    }

    function scrollKiosk (reset = false) {
      if (!vm.$playlist) {
        return
      }
      if (reset) {
        vm.$playlist.style.top = '0px'
      } else {
        var currentPosition = parseInt(vm.$playlist.style.top, 10)
        vm.$playlist.style.top = (currentPosition - 1).toString() + 'px'
      }
    }

    function silentSuccess (allNewItems) {
      var contentToAdd = allNewItems.filter(function (newItem) {
        var exists = vm.allItems.filter(function (existingItem) {
          return existingItem.id === newItem.id
        })
        if (exists >= 0) {
          return newItem
        }
      })

      var contentIdsToRemove = []
      vm.allItems.filter(function (oldItem) {
        var exists = allNewItems.filter(function (existingItem) {
          return existingItem.id === oldItem.id
        })
        if (exists.length === 0) {
          contentIdsToRemove.push(oldItem.id)
          return oldItem
        }
      })
      if (contentIdsToRemove.length || contentToAdd.length) {
        vm.items = []
        vm.items = allNewItems
        vm.allItems = angular.copy(vm.items)
        vm.getKioskGroups()
        Content.emptyContents(vm.gridId)
        Content.addContents(vm.items)
        resetAutoKiosk(true)
      } else {
        resetAutoKiosk(false)
      }
    }

    function silentFail (err) {
      console.log('error', err)
      initKioskAutoRefresh()
    }
  }

  module.exports = MODULE_NAME
})()



// WEBPACK FOOTER //
// ./src/playlist/playlist.js